import { useLocation, useNavigate } from "react-router";

import { CasosDelLaboratorio } from "@/components/laboratorio/CasosDelLaboratorio";
import { ExperimentosDelLaboratorio } from "@/components/laboratorio/ExperimentosDelLaboratorio";
import { RegistroDeCorridas } from "@/components/laboratorio/RegistroDeCorridas";
import { PromptsDelLaboratorio } from "@/components/laboratorio/PromptsDelLaboratorio";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCasos, usePromptsDelLaboratorio, useResumenDelMes, useTotalDeCorridas } from "@/hooks/useLaboratorio";
import { formatearCostoUsd, formatearTokens } from "@/lib/formatoDeConsumo";
import { formatearPorcentajeDeError, tabDelLaboratorio, type TabDelLaboratorio } from "@/lib/laboratorio";

/**
 * Laboratorio de IA (`/admin/ia/laboratorio`): prompts con versiones, casos de
 * prueba congelados, experimentos y la traza de cada corrida. Super Admin.
 *
 * La pestaña vive en el HASH de la url y lo demás (la función a probar, el
 * experimento abierto) en la búsqueda: así recargar o compartir el enlace cae
 * en el mismo lugar.
 */
export function LaboratorioDeIa() {
  const { hash, search } = useLocation();
  const navigate = useNavigate();
  const tab = tabDelLaboratorio(hash);

  const resumen = useResumenDelMes();
  const prompts = usePromptsDelLaboratorio();
  const casos = useCasos();
  const totalDeCorridas = useTotalDeCorridas();

  function irA(destino: TabDelLaboratorio, busqueda = "") {
    void navigate({ hash: destino, search: busqueda }, { replace: false });
  }

  return (
    <div className="flex flex-col gap-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-ink-muted max-w-[44ch] text-[0.75rem] font-semibold">
          Prompts con versiones, casos de prueba congelados, experimentos y la traza de cada corrida.
        </p>
        <dl className="flex items-start gap-6">
          <Indicador etiqueta="Costo este mes" valor={resumen.data ? formatearCostoUsd(String(resumen.data.costo_usd)) : "—"} />
          <Indicador etiqueta="Llamadas este mes" valor={resumen.data ? formatearTokens(resumen.data.llamadas) : "—"} />
          <Indicador etiqueta="Con error" valor={resumen.data ? formatearPorcentajeDeError(resumen.data.porcentaje_con_error) : "—"} />
        </dl>
      </div>

      <Tabs variant="underline-ink" value={tab} onValueChange={(v) => irA(v as TabDelLaboratorio, v === tab ? search : "")} className="gap-4">
        <TabsList className="gap-5">
          <TabsTrigger value="prompts">
            Prompts <Contador valor={prompts.data?.data.length} />
          </TabsTrigger>
          <TabsTrigger value="casos">
            Casos de prueba <Contador valor={casos.data?.length} />
          </TabsTrigger>
          <TabsTrigger value="experimentos">Experimentos</TabsTrigger>
          <TabsTrigger value="corridas">
            Registro de corridas <Contador valor={totalDeCorridas.data} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prompts">
          <PromptsDelLaboratorio onProbar={(funcion) => irA("experimentos", `?funcion=${funcion}&nuevo=1`)} />
        </TabsContent>
        <TabsContent value="casos">
          <CasosDelLaboratorio onIrALasCorridas={() => irA("corridas")} />
        </TabsContent>
        <TabsContent value="experimentos">
          <ExperimentosDelLaboratorio />
        </TabsContent>
        <TabsContent value="corridas">
          <RegistroDeCorridas onCasoCreado={() => irA("casos")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Indicador({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <dd className="text-ink order-1 font-mono text-[0.9375rem] font-bold">{valor}</dd>
      <dt className="text-ink-medio order-2 text-[0.65625rem] font-bold">{etiqueta}</dt>
    </div>
  );
}

function Contador({ valor }: { valor: number | undefined }) {
  return valor === undefined ? null : <span className="text-ink-medio font-mono text-[0.6875rem] font-bold">{valor}</span>;
}
